// Quick start — the "get set up" walkthrough, moved from the old standalone
// /quickstart page into the docs. The web app browses vaults read-only;
// editing + sync happen in Obsidian via the `cairn` CLI, so this walks a new
// user from installing the CLI to opening a synced vault. Command names are
// kept in step with specs/cli.md §5.

import {
  IconBook2,
  IconCloudDownload,
  IconDownload,
  IconFolders,
  IconRefresh,
  IconTerminal2,
} from "@tabler/icons-react";
import { serverOrigin } from "@/lib/server-origin";
import { CommandLine } from "@/components/command-line";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DocSection, K, P } from "../doc-ui";

export function QuickstartDoc() {
  const origin = serverOrigin();
  // Pre-fill the server URL as a flag so the copied command skips the
  // interactive server prompt. Same `origin` the install one-liner uses, and
  // the single source for both the rendered text and the clipboard payload.
  const loginCommand = `cairn login --server ${origin}`;

  return (
    <>
      <Alert>
        <IconBook2 />
        <AlertTitle>You're in the read-only web app</AlertTitle>
        <AlertDescription>
          This web app browses your vaults read-only. To edit and sync, use the Cairn plugin for
          Obsidian, set up with the <K>cairn</K> command-line tool.
        </AlertDescription>
      </Alert>

      <DocSection
        icon={IconDownload}
        title="1. Install the CLI"
        description={
          <>
            One line installs <K>cairn</K> to <K>~/.local/bin</K> — no sudo (override with{" "}
            <K>CAIRN_INSTALL_DIR</K>). macOS (Apple Silicon or Intel) and Linux x86_64.
          </>
        }
      >
        <CommandLine command={`curl -fsSL ${origin}/install.sh | sh`} />
        <P>Then sign in on this machine:</P>
        <CommandLine command={loginCommand} />
        <P>
          The server URL (<K>{origin}</K>) is already filled in — just enter your email and
          password, plus your TOTP code if you've enrolled two-factor.
        </P>
      </DocSection>

      <DocSection
        icon={IconFolders}
        title="2. Get a vault into Obsidian"
        description="The CLI creates the folder and installs the plugin for you — you don't install anything in Obsidian by hand."
      >
        <CommandLine command="cairn vault list" comment="see the vaults you can access" />
        <CommandLine
          command="cairn vault clone <vault>"
          comment="create the folder + install & configure the plugin"
        />
        <P>Already have a local folder? Attach it instead:</P>
        <CommandLine
          command="cairn vault link <path>"
          comment="attach an existing folder to a vault"
        />
        <Alert>
          <IconTerminal2 />
          <AlertTitle>Then open it in Obsidian</AlertTitle>
          <AlertDescription>
            Open that folder as a vault in Obsidian — the Cairn plugin syncs it live. The CLI
            already installed and configured the plugin from your server, so there's nothing to add
            via BRAT or the community plugins list.
          </AlertDescription>
        </Alert>
      </DocSection>

      <DocSection
        icon={IconRefresh}
        title={
          <>
            Staying current{" "}
            <span className="font-mono text-xs font-normal text-muted-foreground">optional</span>
          </>
        }
        description="Keep the CLI and the installed plugin fresh."
      >
        <CommandLine command="cairn update" comment="self-update the CLI" />
        <CommandLine command="cairn plugin update" comment="update the installed Obsidian plugin" />
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <IconCloudDownload className="size-4 shrink-0" />
          Updates come from the same server you already signed in to.
        </p>
      </DocSection>
    </>
  );
}
